import { Permissions } from '../enums/permissions';

/**
 * What the API says about the signed-in user: what they hold, by service center. ALL is every service center.
 */
export interface SessionRead {
    userId?: number;
    email?: string;
    displayName?: string;
    permissions?: { [serviceCenter: string]: Permissions[] };
}

export class AppUser {

    public email            : string;
    public fullName         : string;
    public firstName        : string;
    public lastName         : string;

    /** What the user holds in each service center, and for ALL of them under ALL */
    public permissionsByServiceCenter : { [serviceCenter: string]: Permissions[] } = {};

    /** What the user holds in the service center they are working in */
    public permissions      : Permissions[] = [];

    /** The service centers the user can work in; ALL is every one of them */
    public serviceCenters   : string[] = [];

    /**
     * @param userData The claims of the id token: who the user is. What they may do comes from the API, see withSession
     */
    constructor(userData?:any) {

        if(userData){

            this.email          = userData.email;
            this.firstName      = userData.given_name;
            this.lastName       = userData.family_name;
            this.fullName       = userData.name;
        }
    }

    /**
     * The user, with what the API says they hold
     * @param user Who the user is
     * @param session What they hold, by service center
     */
    public static withSession(user: AppUser, session: SessionRead | undefined): AppUser {

        const byCenter = (session && session.permissions) ? session.permissions : {};
        const centers = Object.keys(byCenter);

        // ALL covers every service center, so there is nothing to list beside it
        const serviceCenters = centers.some(x => x.toUpperCase() === 'ALL') ? ['ALL'] : centers;

        const result: AppUser = Object.assign(new AppUser(), user, {
            permissionsByServiceCenter: byCenter,
            serviceCenters
        });

        result.permissions = AppUser.permissionsIn(byCenter, serviceCenters[0]);
        return result;
    }

    /**
     * Whether a user can work in a service center: in any of them when they can work in ALL, otherwise in the ones they were given
     * @param serviceCenters The service centers the user can work in
     * @param serviceCenter The one they want to work in
     */
    public static canWorkIn(serviceCenters: string[] | undefined, serviceCenter: string | undefined): boolean {

        if (!serviceCenters || !serviceCenter) {
            return false;
        }

        return serviceCenters.some(x => x.toUpperCase() === 'ALL' || x.toUpperCase() === serviceCenter.toUpperCase());
    }

    /**
     * What a user holds in a service center: what they were given for ALL of them, and what they were given for that one
     * @param byServiceCenter What the user holds, by service center
     * @param serviceCenter The service center they are working in
     */
    public static permissionsIn(byServiceCenter: { [serviceCenter: string]: Permissions[] } | undefined, serviceCenter: string | undefined): Permissions[] {

        const held = new Set<Permissions>();
        Object.keys(byServiceCenter || {}).forEach(key => {
            const applies = key.toUpperCase() === 'ALL' || (!!serviceCenter && key.toUpperCase() === serviceCenter.toUpperCase());
            if (applies) {
                (byServiceCenter![key] || []).forEach(x => held.add(x));
            }
        });

        return Array.from(held);
    }

}
